/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component, Inject, OnInit} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {FabMessageService} from '@fabric/components/message';
import {DISMISS_MSG, VIEW_AUTHORITY} from '../../../../../common/constants';
import {LoadingCounter} from '../../../../../core/helpers/loading-counter';
import {StateService} from '../../../../../core/state/state.service';
import {WhiteSpaceInputValidator} from '../../../../../core/validators/white-space-input-validator';
import {IndustryScenario} from '../../../../../models/industry-scenario-dto.model';
import {ScenarioCategory} from '../../../../../models/scenario-category.model';
import {ScenarioType} from '../../../../../models/scenario-type.model';
import {IndustryScenarioService} from '../../../../../services/industry-scenario.service';


@Component({
    selector: 'app-edit-industry-scenario-popup',
    templateUrl: './edit-industry-scenario-popup.component.html',
    styleUrls: ['./edit-industry-scenario-popup.component.scss'],
    standalone: false
})
export class EditIndustryScenarioPopupComponent implements OnInit {

  readonly loading = new LoadingCounter();
  errorMessage = '';

  industryScenarioForm: FormGroup;
  industryScenario: IndustryScenario;
  updatedIndustryScenario: IndustryScenario;

  scenarioCategories: ScenarioCategory[] = [];
  scenarioTypes: ScenarioType[] = [];
  isAdmin: boolean;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: IndustryScenario,
    public dialogRef: MatDialogRef<any>,
    public snackbar: FabMessageService,
    private readonly industryScenarioService: IndustryScenarioService,
    private readonly userService: StateService) {
  }

  get showSpinner(): boolean {
    return this.loading.active;
  }

  /**
   * Load scenario categories and scenario types
   * Make industryScenarioForm with whitespace check validation
   */
  ngOnInit(): void {
    this.isAdmin = this.userService.hasAuthority(VIEW_AUTHORITY.centralOfficeAdminAuthority);
    this.industryScenario = this.data;
    this.loadScenarioCategories();
    this.loadScenarioTypes();
    this.industryScenarioForm = new FormGroup({
      updatedScenarioName: new FormControl(this.updatedIndustryScenario, [Validators.required,
        WhiteSpaceInputValidator.noWhitespaceValidator]),
      updatedIndustryScenarioCategory: new FormControl(this.updatedIndustryScenario, [Validators.required]),
      updatedIndustryScenarioType: new FormControl(this.updatedIndustryScenario, [Validators.required]),
      updatedScenarioAssumption: new FormControl(this.updatedIndustryScenario, [Validators.required,
        WhiteSpaceInputValidator.noWhitespaceValidator]),
      updatedIsDeleteLocked: new FormControl(this.industryScenario.isDeleteLocked, [Validators.required]),
      updatedIsEditLocked: new FormControl(this.industryScenario.isEditLocked, [Validators.required]),
      updatedIsHiddenLocked: new FormControl(this.industryScenario.isHidden, [Validators.required])
    });
  }

  /**
   * Fetch a list of all the scenario categories from service
   */
  loadScenarioCategories(): void {
    this.loading.track(this.industryScenarioService.getAllScenarioCategories()).subscribe(
      (data) => {
        this.scenarioCategories = data;
      },
      (error) => {
        this.errorHandle(error);
      }
    );
  }

  /**
   * Fetch a list of all the scenario types from service
   */
  loadScenarioTypes(): void {
    this.loading.track(this.industryScenarioService.getAllScenarioTypes()).subscribe(
      (data) => {
        this.scenarioTypes = data;
      },
      (error) => {
        this.errorHandle(error);
      }
    );
  }

  /**
   * Saves the all the updated values to industryScenario. calls updateIndustryScenario from service
   * with the updated industryScenario
   */
  onSubmit(): void {
    this.industryScenario.industryScenarioName = this.industryScenarioForm.get('updatedScenarioName').value;
    this.industryScenario.industryScenarioCategory = this.industryScenarioForm
    .get('updatedIndustryScenarioCategory').value;
    this.industryScenario.industryScenarioType = this.industryScenarioForm.get('updatedIndustryScenarioType').value;
    this.industryScenario.industryScenarioAssumption = this.industryScenarioForm
    .get('updatedScenarioAssumption').value;
    this.industryScenario.isDeleteLocked = this.industryScenarioForm.get('updatedIsDeleteLocked').value;
    this.industryScenario.isEditLocked = this.industryScenarioForm.get('updatedIsEditLocked').value;
    this.industryScenario.isHidden = this.industryScenarioForm.get('updatedIsHiddenLocked').value;
    this.loading.track(this.industryScenarioService.updateIndustryScenario(this.industryScenario)).subscribe({
      error: (error) => {
        this.errorHandle(error);
      },
      complete: () => {
        this.snackbar.displaySuccess('Successfully updated industry scenario', DISMISS_MSG);
        this.onCancel();
      }
    });
  }

  /**
   * On cancel close the dialog
   */
  onCancel(): void {
    this.dialogRef.close();
  }

  /**
   * display message on error.
   * @param error custom error message.
   */
  errorHandle(error: string): void {
    this.snackbar.displayError(error, DISMISS_MSG);
  }
}
